import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui';
import { Menu, X, Globe, LogOut, LayoutDashboard, User, ArrowLeft, Shield } from 'lucide-react';
import { useState, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { useAuth, useClickOutsideOrEscape } from '../../hooks';
import { useAdmin } from '../../hooks/useAdmin';
import logoImg from '../../assets/logo.png';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'it', name: 'Italiano' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
];

export function Header() {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isAdmin = location.pathname.startsWith('/admin');
  const { isSuperAdmin } = useAdmin();

  const closeLangDropdown = useCallback(() => setIsLangOpen(false), []);
  const closeUserDropdown = useCallback(() => setIsUserMenuOpen(false), []);
  
  const langRef = useClickOutsideOrEscape<HTMLDivElement>(closeLangDropdown, isLangOpen);
  const userMenuRef = useClickOutsideOrEscape<HTMLDivElement>(closeUserDropdown, isUserMenuOpen);

  const navLinks = [
    { href: '/#features', label: t('nav.features') },
    { href: '/#roles', label: t('nav.roles') },
    { href: '/#pricing', label: t('nav.pricing') },
  ];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setIsLangOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Mobile back button */}
          {(isDashboard || isAdmin) && location.pathname !== '/dashboard' && location.pathname !== '/admin' && (
            <button
              onClick={() => navigate(-1)}
              className="md:hidden text-muted-foreground hover:text-foreground transition-colors p-1 mr-1"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          {/* Mobile admin button */}
          {isSuperAdmin && isDashboard && (
            <Link to="/admin" className="md:hidden text-muted-foreground hover:text-accent transition-colors p-1 mr-1">
              <Shield size={18} />
            </Link>
          )}
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2">
            <img src={logoImg} alt="SnapAgent" className="w-12 h-12" width={48} height={48} fetchPriority="high" />
            <span className="text-xl font-semibold text-foreground">SnapAgent</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {isLanding && !user && navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
              >
                {link.label}
              </a>
            ))}
            {isDashboard && user && (
              <>
                <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium">
                  {t('nav.dashboard')}
                </Link>
              </>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {isSuperAdmin && !isAdmin && (
              <Link to="/admin" className="flex items-center gap-1.5 text-muted-foreground hover:text-accent transition-colors text-sm">
                <Shield size={16} />
                <span>Admin</span>
              </Link>
            )}
            <div className="relative" ref={langRef}>
              <button
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors text-sm"
                onClick={() => setIsLangOpen(!isLangOpen)}
              >
                <Globe size={16} />
                <span className="uppercase">{i18n.language.slice(0, 2)}</span>
              </button>
              {isLangOpen && (
                <div className="absolute top-full right-0 mt-2 bg-card border border-border rounded-lg shadow-lg py-2 min-w-[140px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={cn(
                        "w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors",
                        i18n.language.startsWith(lang.code) ? "text-accent" : "text-muted-foreground"
                      )}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                    <User size={16} className="text-accent" />
                  </div>
                </button>
                {isUserMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-card border border-border rounded-lg shadow-lg py-2 min-w-[180px] z-50">
                    <div className="px-4 py-2 border-b border-border-subtle">
                      <p className="text-xs text-foreground-muted">Signed in as</p>
                      <p className="text-sm text-foreground truncate">{user.email}</p>
                    </div>
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <LayoutDashboard size={16} />
                      {t('nav.dashboard')}
                    </Link>
                    <button
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-error hover:bg-muted transition-colors"
                      onClick={handleSignOut}
                    >
                      <LogOut size={16} />
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">{t('nav.login')}</Button>
                </Link>
                <Link to="/register">
                  <Button variant="accent" size="sm">{t('nav.signup')}</Button>
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden text-foreground p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="md:hidden bg-card border-b border-border-subtle overflow-hidden"
          >
            <div className="px-4 py-4 space-y-4">
          {isLanding && !user && navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          
          <div className="py-2">
            <p className="text-xs text-foreground-muted mb-2">Language</p>
            <div className="flex flex-wrap gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={cn(
                    "px-3 py-1.5 text-sm rounded-lg border transition-colors",
                    i18n.language.startsWith(lang.code) 
                      ? "border-accent text-accent bg-accent/10" 
                      : "border-border text-muted-foreground hover:border-accent"
                  )}
                  onClick={() => changeLanguage(lang.code)}
                >
                  {lang.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          
          <div className="pt-4 border-t border-border-subtle flex flex-col gap-2">
            {user ? (
              <>
                <div className="px-2 py-2 text-sm text-foreground-muted">
                  {user.email}
                </div>
                <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start gap-2">
                    <LayoutDashboard size={16} />
                    {t('nav.dashboard')}
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start gap-2 text-error"
                  onClick={() => { handleSignOut(); setIsMenuOpen(false); }}
                >
                  <LogOut size={16} />
                  {t('nav.logout')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="ghost" className="w-full">{t('nav.login')}</Button>
                </Link>
                <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="accent" className="w-full">{t('nav.signup')}</Button>
                </Link>
              </>
            )}
          </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
